/**
 * Day bucketing for a thread — pure, order-preserving.
 *
 * The thread arrives oldest-first, so equal days are already contiguous: a run-length
 * pass keeps the original order without a second sort. The legacy surfaces used
 * `lodash.groupBy` + `Object.entries().sort()`, which re-derives an ordering the input
 * already had and silently drops it when two days share a formatted label.
 */

import { formatDate } from "@/libs/date-fns";

export type MessageDayGroup<TMessage> = {
  /** `yyyy-MM-dd` in the viewer's timezone — what `LineAndDate` formats. */
  date: string;
  messages: TMessage[];
};

export function groupMessagesByDay<TMessage extends { createdAt: string }>(
  messages: TMessage[],
): MessageDayGroup<TMessage>[] {
  const groups: MessageDayGroup<TMessage>[] = [];

  for (const message of messages) {
    const date = message.createdAt
      ? formatDate(message.createdAt, "yyyy-MM-dd")
      : "";
    const currentGroup = groups.at(-1);

    if (currentGroup?.date === date) {
      currentGroup.messages.push(message);
      continue;
    }

    groups.push({ date, messages: [message] });
  }

  return groups;
}
